import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Button from '../Button/Button';
import styles from './ActionMenu.module.scss';

export interface ActionMenuItem {
  label: string;
  onSelect: () => void;
  /** Renders in the error colour — for the destructive entry, conventionally last. */
  danger?: boolean;
  disabled?: boolean;
  /** Native tooltip. Use it to say *why* a disabled item is unavailable. */
  title?: string;
  /** Draws a separator above this item. */
  dividerBefore?: boolean;
}

/** Callers build lists with inline conditionals, so falsy entries are dropped. */
export type ActionMenuEntry = ActionMenuItem | false | null | undefined;

interface ActionMenuProps {
  items: ActionMenuEntry[];
  /** Trigger text. Defaults to "Actions". */
  label?: string;
  /** Names the menu for screen readers, e.g. "Actions for Juniper". */
  ariaLabel?: string;
}

/** Gap between the trigger and the menu, and the minimum breathing room at the viewport edge. */
const GAP = 4;
const EDGE = 8;
/** Never clamp below this, or a menu in a very short viewport becomes unusable rather than scrollable. */
const MIN_HEIGHT = 64;

/**
 * Ties a disabled entry to the text explaining it, via aria-describedby. Derived from the
 * label rather than a counter because only one menu is ever open at a time — opening another
 * closes the first — so these ids cannot collide across rows.
 */
function reasonId(label: string): string {
  return `action-menu-reason-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * A single button that opens a list of row actions.
 *
 * Collapses a row of buttons into one control, which is what lets a wide table give its
 * Action column back to the columns carrying data. Modelled on the Dashboard's device/chip
 * menus, but portalled rather than absolutely positioned: the Devices tables sit inside an
 * `overflow-x: auto` wrapper, and an in-flow menu would be clipped by it. The Dashboard
 * avoids that by setting `overflow: visible` on its wrapper — giving up horizontal scroll,
 * which a table this wide can't afford.
 */
export default function ActionMenu({ items, label = 'Actions', ariaLabel }: ActionMenuProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number; maxHeight: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const visible = items.filter((item): item is ActionMenuItem => Boolean(item));

  /**
   * Anchors the menu to the trigger in viewport coordinates, flipping above it when the
   * space below runs out. Reads the menu's real height when it is already mounted, so the
   * flip doesn't depend on item heights hard-coded to match the stylesheet.
   */
  const position = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    // `clientWidth`/`clientHeight`, not `innerWidth`/`innerHeight`: getBoundingClientRect
    // reports client coordinates, which exclude a classic scrollbar, while the `inner*`
    // pair includes it. Mixing them puts the menu a scrollbar's width left of its trigger
    // anywhere scrollbars take up space — invisible on macOS overlay scrollbars, plainly
    // wrong on Windows.
    const { clientWidth, clientHeight } = document.documentElement;
    const roomBelow = clientHeight - rect.bottom - GAP - EDGE;
    const roomAbove = rect.top - GAP - EDGE;
    // How tall the menu WANTS to be. Deliberately not `offsetHeight`: by the time this runs
    // again the menu is already carrying the `maxHeight` set on the pass before, so
    // offsetHeight reports the clamped value and every comparison below degenerates to
    // `room < room` — the flip would never fire, leaving the menu a scroll box wedged into a
    // gap while there was room to open into above it. `scrollHeight` sees past the clamp;
    // the difference between offset and client height adds the border back on.
    const menu = menuRef.current;
    const natural = menu ? menu.scrollHeight + (menu.offsetHeight - menu.clientHeight) : 0;
    // Flip only when above genuinely has more room, so a viewport too short for either side
    // still lands on the roomier one rather than always opening downward.
    const flip = natural > 0 && roomBelow < natural && roomAbove > roomBelow;
    // Clamped to what is actually there: with neither side tall enough the menu would
    // otherwise run off the viewport, and being `position: fixed` its hidden entries could
    // not be scrolled to at all.
    const room = Math.max(flip ? roomAbove : roomBelow, MIN_HEIGHT);
    // Flipping positions from the menu's top edge, so it has to be the height that will
    // actually render — the natural one where it fits, the clamp where it does not.
    const shown = natural > 0 ? Math.min(natural, room) : room;
    setCoords({
      top: flip ? Math.max(EDGE, rect.top - shown - GAP) : rect.bottom + GAP,
      right: clientWidth - rect.right,
      maxHeight: room,
    });
  }, []);

  // Positioned before the menu renders (from the click handler) so that by the time this
  // effect runs the menu is already in the DOM and can be measured for the flip.
  // Capture-phase scroll catches scrolls on any ancestor, including the table's own
  // horizontal scroller, so the menu tracks its row instead of detaching from it.
  useLayoutEffect(() => {
    if (!open) {
      return;
    }
    position();
    window.addEventListener('scroll', position, true);
    window.addEventListener('resize', position);
    return () => {
      window.removeEventListener('scroll', position, true);
      window.removeEventListener('resize', position);
    };
  }, [open, position]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handlePointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  /**
   * The items that can actually take focus. Disabled buttons are not focusable, so calling
   * `.focus()` on one is a silent no-op — walk past them or arrowing down a menu with a
   * disabled entry simply stops there.
   */
  function focusableItems(): HTMLButtonElement[] {
    return Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? []);
  }

  // Opening moves focus into the menu: the portal renders at the end of <body>, so without
  // this the next Tab would land somewhere unrelated to the row that was just acted on.
  useEffect(() => {
    if (open) {
      focusableItems()[0]?.focus();
    }
  }, [open]);

  function close(refocus: boolean) {
    setOpen(false);
    if (refocus) {
      triggerRef.current?.focus();
    }
  }

  function handleMenuKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
      return;
    }
    e.preventDefault();
    const buttons = focusableItems();
    if (buttons.length === 0) {
      return;
    }
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    // With focus outside the list (index -1) the modular step would land on the
    // second-to-last item going up, so enter from the appropriate end instead.
    const next =
      index < 0
        ? buttons[step === 1 ? 0 : buttons.length - 1]
        : buttons[(index + step + buttons.length) % buttons.length];
    next?.focus();
  }

  if (visible.length === 0) {
    return null;
  }

  const menu =
    open && coords
      ? ReactDOM.createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label={ariaLabel}
            style={{ position: 'fixed', top: coords.top, right: coords.right, maxHeight: coords.maxHeight }}
            onKeyDown={handleMenuKeyDown}
          >
            {visible.map((item) => (
              <React.Fragment key={item.label}>
                {item.dividerBefore && <div className={styles.divider} role="separator" />}
                {/* The title rides on the wrapper, not the button: browsers suppress pointer
                    events on a disabled control, so a title there never surfaces — and the
                    disabled case is the only one with a reason worth reading. `role="none"`
                    keeps the wrapper out of the accessibility tree, so the menu still owns
                    its menuitems directly; a generic div between them would leave the menu
                    reading as empty and drop menu-mode arrow navigation. */}
                <div className={styles.itemWrap} role="none" title={item.title}>
                  <button
                    type="button"
                    role="menuitem"
                    className={`${styles.item}${item.danger ? ` ${styles.itemDanger}` : ''}`}
                    disabled={item.disabled}
                    aria-describedby={item.disabled && item.title ? reasonId(item.label) : undefined}
                    onClick={() => {
                      // Focus goes back to the trigger, not nowhere: several entries only
                      // navigate (a mailto:) or toast, so nothing else would claim it and a
                      // keyboard user would restart from the top of the document. Entries
                      // that open a modal simply take focus from here a moment later.
                      close(true);
                      item.onSelect();
                    }}
                  >
                    {item.label}
                  </button>
                  {item.disabled && item.title && (
                    <div id={reasonId(item.label)} className={styles.itemReason}>
                      {item.title}
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <Button
        ref={triggerRef}
        variant="secondary"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => {
          if (open) {
            close(false);
            return;
          }
          position();
          setOpen(true);
        }}
      >
        {label}
        <span className={styles.chevron} aria-hidden="true">
          &#9662;
        </span>
      </Button>
      {menu}
    </>
  );
}
